import type { ComponentProps, ReactNode } from 'react';

import { MultiSelect } from '#/components/Select/MultiSelect';
import type { OptionType } from '#/components/Select/Select.types';

import { Field } from './Field/Field';
import type { FieldCustomErrorMatcher } from './Field/FieldCustomErrorMatcher';

type Props<T = string> = {
  qaId?: string;
  maxSelected?: number | null | undefined;
  disabled?: boolean;
  name: string;
  label?: string;
  hint?: ReactNode;
  description?: ReactNode;
  options: OptionType<T>[];
  onChange?: (value: T[]) => void;
  onFocus?: () => void;
  placeholder?: string;
  menuPortalTarget?: ComponentProps<typeof MultiSelect>['menuPortalTarget'];
  menuPosition?: ComponentProps<typeof MultiSelect>['menuPosition'];
  customErrorMatch?: FieldCustomErrorMatcher;
};

export function MultiSelectField<T = string>({
  name,
  label,
  description,
  options,
  hint,
  onChange,
  onFocus,
  menuPortalTarget,
  menuPosition,
  qaId,
  customErrorMatch,
  ...props
}: Props<T>) {
  return (
    <Field
      inputProps={{ ...props, options }}
      name={name}
      label={label}
      description={description}
      customErrorMatch={customErrorMatch}
      renderInput={({ value, id, qaId: fieldQaId, updateValue }) => (
        <>
          {hint ?? null}
          <MultiSelect<T>
            id={id}
            name={name}
            value={value as T[] | null}
            options={options}
            qaId={qaId ?? fieldQaId}
            onChange={(e) => {
              // Ensure we're not losing values when updating
              updateValue(e);

              if (onChange) {
                onChange(e);
              }
            }}
            onFocus={onFocus}
            label={label}
            menuPortalTarget={menuPortalTarget}
            menuPosition={menuPosition}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        </>
      )}
    />
  );
}
